// subscriptionService.ts
// RevenueCat subscription management service
// Handles initialization, purchases, restore, and sync with Firestore

import { Platform } from 'react-native';
import { SubscriptionTier } from '../models/PrivateProfile';
import { auth } from '../config/firebase';
import { errorReporting } from './errorReportingService';

// Conditional import for RevenueCat (not available on web)
// This uses webpack's aliasing to mock on web
let Purchases: any = null;
let LOG_LEVEL: any = { INFO: 'INFO' };

try {
  if (Platform.OS !== 'web') {
    const RCModule = require('react-native-purchases');
    Purchases = RCModule.default || RCModule;
    LOG_LEVEL = RCModule.LOG_LEVEL;
    console.log('[SubscriptionService] RevenueCat module loaded successfully');
  }
} catch (error) {
  console.error('[SubscriptionService] Failed to load RevenueCat module:', error);
  if (Platform.OS !== 'web') {
    console.error('[SubscriptionService] Make sure react-native-purchases is installed and pods are updated');
  }
}

// Type definitions
export interface CustomerInfo {
  entitlements: {
    active: { [key: string]: EntitlementInfo };
    all: { [key: string]: EntitlementInfo };
  };
  originalAppUserId: string;
  firstSeen: string;
  requestDate: string;
  managementURL?: string | null; // URL to manage subscription (RevenueCat provides this)
}

export interface EntitlementInfo {
  identifier: string;
  isActive: boolean;
  willRenew: boolean;
  productIdentifier: string;
  expirationDate: string | null;
  originalPurchaseDate: string | null;
  periodType: 'normal' | 'trial' | 'intro';
  store?: string; // 'app_store' | 'play_store' | 'stripe' | 'promotional'
}

export interface PurchasesPackage {
  identifier: string;
  packageType: string;
  product: StoreProduct;
}

export interface StoreProduct {
  identifier: string;
  description: string;
  title: string;
  price: number;
  priceString: string;
  currencyCode: string;
  introPrice?: IntroPrice; // Intro/trial pricing info
}

export interface IntroPrice {
  price: number;
  priceString: string;
  cycles: number;
  period: string; // e.g., "P1W" for 1 week, "P1M" for 1 month
  periodUnit: string; // "DAY", "WEEK", "MONTH", "YEAR"
  periodNumberOfUnits: number;
}

export interface PurchasesOffering {
  identifier: string;
  availablePackages: PurchasesPackage[];
}

export interface PurchasesOfferings {
  current: PurchasesOffering | null;
  all: { [key: string]: PurchasesOffering };
}

// RevenueCat API Key (same for iOS and Android)
const REVENUECAT_API_KEY = 'TNYkGDDxgECpoweRjJjANMmqEDjhcDrY';

// Entitlement IDs (configured in RevenueCat dashboard)
// Simple and clear structure:
//   - 'platinum' entitlement → standard_premium tier (Platinum - content only, no AI)
//   - 'pro' entitlement → standard_premium tier (Legacy - same as Platinum)
//   - 'ai-platinum' entitlement → ai_premium tier (AI Platinum - content + AI)
const ENTITLEMENT_PLATINUM = 'platinum';       // Grants content access (Platinum tier)
const ENTITLEMENT_PRO = 'pro';                 // Legacy entitlement (same as Platinum)
const ENTITLEMENT_AI_PLATINUM = 'ai-platinum'; // Grants AI + content access (AI Platinum tier)

export interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface SubscriptionInfo {
  tier: SubscriptionTier;
  expiresAt: number | null;
  isActive: boolean;
  willRenew: boolean;
  customerId?: string;
  source?: string; // 'app_store' | 'play_store' | 'stripe' | 'promotional' | 'unknown'
  managementURL?: string | null; // URL to manage subscription
}

export interface SubscriptionPackage {
  identifier: string;
  packageType: string;
  product: {
    identifier: string;
    description: string;
    title: string;
    price: number;
    priceString: string;
    currencyCode: string;
  };
}

/**
 * SubscriptionService
 * Manages RevenueCat integration for subscription management
 * Pattern copied from iOS: Purchases.shared.logIn(firebaseId)
 */
export class SubscriptionService {
  private static isInitialized = false;
  private static customerInfoCache: CustomerInfo | null = null;
  private static lastSyncTime = 0;
  private static readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Initialize RevenueCat SDK
   * Should be called on app start after authentication
   * iOS pattern: Same API key for both platforms
   */
  static async initialize(): Promise<ServiceResponse<boolean>> {
    try {
      // Skip initialization on web
      if (Platform.OS === 'web') {
        console.log('[SubscriptionService] Skipping RevenueCat init on web');
        return { success: true, data: true };
      }

      if (this.isInitialized) {
        return { success: true, data: true };
      }

      // Check if Purchases module is available
      if (!Purchases) {
        const error = new Error('RevenueCat Purchases module not available. Make sure react-native-purchases is installed and linked.');
        console.error('[SubscriptionService]', error.message);
        errorReporting.reportError(error, {
          screen: 'SubscriptionService',
          action: 'initialize',
        });
        return { success: false, error: error.message };
      }

      // Configure RevenueCat
      // Set to ERROR to suppress product validation warnings for legacy products
      Purchases.setLogLevel(LOG_LEVEL.ERROR);
      
      // Initialize with API key
      await Purchases.configure({ apiKey: REVENUECAT_API_KEY });

      this.isInitialized = true;
      console.log('[SubscriptionService] RevenueCat initialized successfully');

      return { success: true, data: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('[SubscriptionService] Initialization failed:', errorMessage, error);
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'initialize',
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Login user to RevenueCat with Firebase UID
   * iOS pattern: Purchases.shared.logIn(firebaseId)
   */
  static async loginUser(userId?: string): Promise<ServiceResponse<CustomerInfo>> {
    try {
      if (Platform.OS === 'web') {
        return { success: false, error: 'Not supported on web' };
      }

      const uid = userId || auth.currentUser?.uid;
      if (!uid) {
        return { success: false, error: 'No user ID provided or found in Auth' };
      }

      if (!this.isInitialized) {
        await this.initialize();
      }

      console.log('[SubscriptionService] 🔑 Logging into RevenueCat with Firebase UID:', uid);
      const { customerInfo } = await Purchases.logIn(uid);
      this.customerInfoCache = customerInfo;
      this.lastSyncTime = Date.now();

      const activeEntitlements = Object.keys(customerInfo.entitlements.active);
      console.log('[SubscriptionService] ✅ Login successful:', {
        revenueCatCustomerId: customerInfo.originalAppUserId,
        activeEntitlements: activeEntitlements.length > 0 ? activeEntitlements : 'none',
      });
      
      return { success: true, data: customerInfo };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'loginUser',
        userId: userId || 'unknown',
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get current customer info from RevenueCat
   * iOS pattern: retrievePurchasesIfNeeded(requireRefresh: Bool)
   */
  static async getCustomerInfo(forceRefresh = false): Promise<ServiceResponse<CustomerInfo>> {
    try {
      if (Platform.OS === 'web') {
        return { success: false, error: 'Not supported on web' };
      }

      if (!this.isInitialized) {
        await this.initialize();
      }

      // Use cache if fresh and not forcing refresh
      const cacheAge = Date.now() - this.lastSyncTime;
      if (!forceRefresh && this.customerInfoCache && cacheAge < this.CACHE_TTL) {
        return { success: true, data: this.customerInfoCache };
      }

      // Fetch fresh data from RevenueCat
      const customerInfo = await Purchases.getCustomerInfo();
      this.customerInfoCache = customerInfo;
      this.lastSyncTime = Date.now();

      return { success: true, data: customerInfo };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'getCustomerInfo',
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get subscription info from CustomerInfo
   * iOS pattern: hasAnyActiveEntitlement check
   */
  static getSubscriptionInfo(customerInfo: CustomerInfo): SubscriptionInfo {
    // Helper to determine store/source
    const getSource = (entitlement: EntitlementInfo | undefined): string | undefined => {
      // First try to get store from entitlement (preferred)
      if (entitlement?.store) {
        console.log('[SubscriptionService] 📱 Store from entitlement:', entitlement.store);
        return entitlement.store;
      }
      
      // If no store info, infer from current platform
      if (Platform.OS === 'ios') {
        console.log('[SubscriptionService] 📱 Store inferred from platform: app_store (iOS)');
        return 'app_store';
      } else if (Platform.OS === 'android') {
        console.log('[SubscriptionService] 📱 Store inferred from platform: play_store (Android)');
        return 'play_store';
      }
      
      // Fallback to unknown if we can't determine
      console.warn('[SubscriptionService] ⚠️ Could not determine store, Platform.OS:', Platform.OS);
      return 'unknown';
    };

    // Debug logging to track entitlements and products
    const activeEntitlementKeys = Object.keys(customerInfo.entitlements.active);
    console.log('[SubscriptionService] 🔍 Active entitlements:', activeEntitlementKeys);
    
    // Log all active entitlements with their product IDs
    activeEntitlementKeys.forEach(key => {
      const entitlement = customerInfo.entitlements.active[key];
      console.log(`[SubscriptionService] 📦 Entitlement "${key}":`, {
        productId: entitlement?.productIdentifier,
        isActive: entitlement?.isActive,
      });
    });

    // AI-Platinum entitlement = AI Platinum tier (AI + content access) - highest tier
    const aiPlatinum = customerInfo.entitlements.active[ENTITLEMENT_AI_PLATINUM];
    if (aiPlatinum) {
      console.log('[SubscriptionService] ✅ Granting AI PLATINUM tier (ai-platinum entitlement)');
      
      // Log all relevant subscription details for debugging
      console.log('[SubscriptionService] 🔍 Subscription details:', {
        willRenew: aiPlatinum.willRenew,
        expirationDate: aiPlatinum.expirationDate,
        periodType: aiPlatinum.periodType,
        store: aiPlatinum.store,
      });
      
      // Determine expiration: null for lifetime, timestamp for subscriptions
      let expiresAt: number | null = null;
      if (aiPlatinum.expirationDate) {
        const expiryTimestamp = new Date(aiPlatinum.expirationDate).getTime() / 1000;
        // Treat as lifetime if subscription is non-renewing (regardless of source)
        // This covers promotional, lifetime purchases, and non-renewing subscriptions
        if (!aiPlatinum.willRenew) {
          console.log('[SubscriptionService] 🎯 Detected lifetime/non-renewing subscription');
          expiresAt = null; // Lifetime
        } else {
          expiresAt = expiryTimestamp;
        }
      }
      
      return {
        tier: 'ai_premium',
        expiresAt,
        isActive: aiPlatinum.isActive,
        willRenew: aiPlatinum.willRenew,
        customerId: customerInfo.originalAppUserId,
        source: getSource(aiPlatinum),
        managementURL: customerInfo.managementURL,
      };
    }
 
    // Platinum entitlement = Platinum tier (content only, no AI)
    const platinum = customerInfo.entitlements.active[ENTITLEMENT_PLATINUM];
    if (platinum) {
      console.log('[SubscriptionService] ✅ Granting PLATINUM tier (platinum entitlement)');
      
      // Log all relevant subscription details for debugging
      console.log('[SubscriptionService] 🔍 Subscription details:', {
        willRenew: platinum.willRenew,
        expirationDate: platinum.expirationDate,
        periodType: platinum.periodType,
        store: platinum.store,
      });
      
      // Determine expiration: null for lifetime, timestamp for subscriptions
      let expiresAt: number | null = null;
      if (platinum.expirationDate) {
        const expiryTimestamp = new Date(platinum.expirationDate).getTime() / 1000;
        // Treat as lifetime if subscription is non-renewing (regardless of source)
        // This covers promotional, lifetime purchases, and non-renewing subscriptions
        if (!platinum.willRenew) {
          console.log('[SubscriptionService] 🎯 Detected lifetime/non-renewing subscription');
          expiresAt = null; // Lifetime
        } else {
          expiresAt = expiryTimestamp;
        }
      }
      
      return {
        tier: 'standard_premium',
        expiresAt,
        isActive: platinum.isActive,
        willRenew: platinum.willRenew,
        customerId: customerInfo.originalAppUserId,
        source: getSource(platinum),
        managementURL: customerInfo.managementURL,
      };
    }

    // Pro entitlement (legacy) = Platinum tier (same as platinum entitlement)
    const pro = customerInfo.entitlements.active[ENTITLEMENT_PRO];
    if (pro) {
      console.log('[SubscriptionService] ✅ Granting PLATINUM tier (pro entitlement - legacy)');
      
      // Log all relevant subscription details for debugging
      console.log('[SubscriptionService] 🔍 Subscription details:', {
        willRenew: pro.willRenew,
        expirationDate: pro.expirationDate,
        periodType: pro.periodType,
        store: pro.store,
      });
      
      // Determine expiration: null for lifetime, timestamp for subscriptions
      let expiresAt: number | null = null;
      if (pro.expirationDate) {
        const expiryTimestamp = new Date(pro.expirationDate).getTime() / 1000;
        // Treat as lifetime if subscription is non-renewing (regardless of source)
        // This covers promotional, lifetime purchases, and non-renewing subscriptions
        if (!pro.willRenew) {
          console.log('[SubscriptionService] 🎯 Detected lifetime/non-renewing subscription');
          expiresAt = null; // Lifetime
        } else {
          expiresAt = expiryTimestamp;
        }
      }
      
      return {
        tier: 'standard_premium',
        expiresAt,
        isActive: pro.isActive,
        willRenew: pro.willRenew,
        customerId: customerInfo.originalAppUserId,
        source: getSource(pro),
        managementURL: customerInfo.managementURL,
      };
    }

    // Free tier (no active entitlements found)
    const foundAllEntitlements = Object.keys(customerInfo.entitlements.all);
    const foundActiveEntitlements = Object.keys(customerInfo.entitlements.active);
    
    // Only show detailed diagnostics if entitlements exist but aren't recognized (actual problem)
    if (foundAllEntitlements.length > 0) {
      // PROBLEM: User has entitlements but they don't match expected identifiers
      console.warn('[SubscriptionService] ⚠️ ENTITLEMENT IDENTIFIER MISMATCH');
      console.warn('[SubscriptionService] Expected: "ai-platinum", "platinum", or "pro"');
      console.warn('[SubscriptionService] Found entitlements:');
      foundAllEntitlements.forEach(key => {
        const ent = customerInfo.entitlements.all[key];
        console.warn(`  - "${key}":`, {
          isActive: ent.isActive,
          expirationDate: ent.expirationDate,
          productId: ent.productIdentifier,
        });
      });
      console.warn('[SubscriptionService] RevenueCat ID:', customerInfo.originalAppUserId, '| Firebase UID:', auth.currentUser?.uid);
      console.warn('[SubscriptionService] Full customerInfo:', JSON.stringify(customerInfo, null, 2));
    } else {
      // Normal case: New user or user with no purchases
      console.log('[SubscriptionService] No active entitlements found - Free tier');
    }
    
    return {
      tier: 'free',
      expiresAt: null,
      isActive: false,
      willRenew: false,
      customerId: customerInfo.originalAppUserId,
      source: undefined,
      managementURL: undefined,
    };
  }

  /**
   * Get available subscription packages
   */
  static async getOfferings(): Promise<ServiceResponse<PurchasesOfferings>> {
    try {
      if (Platform.OS === 'web') {
        if (__DEV__) {
          // Return mock offerings for web testing (dev only)
          return { success: true, data: this.getMockOfferings() };
        }
        return { success: false, error: 'Purchases not supported on web. Please use iOS or Android.' };
      }

      if (!this.isInitialized) {
        await this.initialize();
      }

      const offerings = await Purchases.getOfferings();
      return { success: true, data: offerings };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'getOfferings',
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Purchase a package
   * iOS pattern: dealer.purchase(package: package)
   */
  static async purchasePackage(pkg: PurchasesPackage): Promise<ServiceResponse<CustomerInfo>> {
    try {
      if (Platform.OS === 'web') {
        if (__DEV__) {
          // Mock successful purchase for web testing (dev only)
          console.log('[SubscriptionService] Mock purchase on web:', pkg.identifier);
          
          // Simulate network delay
          await new Promise(resolve => setTimeout(resolve, 1500));
          
          // Return mock customer info with ai-platinum entitlement (highest tier for testing)
          const mockCustomerInfo: CustomerInfo = {
            entitlements: {
              active: {
                'ai-platinum': {
                  identifier: 'ai-platinum',
                  isActive: true,
                  willRenew: true,
                  productIdentifier: pkg.product.identifier,
                  expirationDate: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
                  originalPurchaseDate: new Date().toISOString(),
                  periodType: pkg.product.introPrice ? 'trial' : 'normal',
                  store: 'app_store',
                },
              },
              all: {},
            },
            originalAppUserId: 'web_test_user',
            firstSeen: new Date().toISOString(),
            requestDate: new Date().toISOString(),
          };
          
          return { success: true, data: mockCustomerInfo };
        }
        return { success: false, error: 'Purchases not supported on web. Please use iOS or Android.' };
      }

      if (!this.isInitialized) {
        await this.initialize();
      }

      // Log purchase details for debugging
      console.log('[SubscriptionService] 🛒 Purchasing package:', {
        packageId: pkg.identifier,
        productId: pkg.product.identifier,
        price: pkg.product.priceString,
      });

      // Make the purchase
      const { customerInfo } = await Purchases.purchasePackage(pkg);
      
      // Update cache
      this.customerInfoCache = customerInfo;
      this.lastSyncTime = Date.now();

      console.log('[SubscriptionService] ✅ Purchase successful');
      return { success: true, data: customerInfo };
    } catch (error: any) {
      // Handle specific RevenueCat errors
      if (error.userCancelled) {
        return { success: false, error: 'Purchase cancelled' };
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Purchase failed';
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'purchasePackage',
        metadata: { packageId: pkg.identifier },
      });
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Restore purchases
   * iOS pattern: restorer.restorePurchases()
   */
  static async restorePurchases(): Promise<ServiceResponse<CustomerInfo>> {
    try {
      if (Platform.OS === 'web') {
        if (__DEV__) {
          // Mock restore for web testing (dev only)
          console.log('[SubscriptionService] Mock restore on web');
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Simulate "no purchases found" for web
          return { success: false, error: 'No purchases found' };
        }
        return { success: false, error: 'Purchases not supported on web. Please use iOS or Android.' };
      }

      if (!this.isInitialized) {
        await this.initialize();
      }

      console.log('[SubscriptionService] 🔄 Attempting to restore purchases...');
      
      const currentFirebaseUid = auth.currentUser?.uid;
      console.log('[SubscriptionService] 🔑 Current Firebase UID:', currentFirebaseUid);
      
      let customerInfo;
      try {
        customerInfo = await Purchases.restorePurchases();
      } catch (restoreError: any) {
        // Capture detailed RevenueCat error - "offline" message is often misleading
        console.error('❌ RevenueCat restorePurchases failed:', restoreError);
        
        errorReporting.reportError(restoreError, {
          userId: auth.currentUser?.uid,
          screen: 'SubscriptionService',
          action: 'restorePurchases',
          metadata: {
            errorCode: restoreError?.code,
            errorMessage: restoreError?.message,
            errorName: restoreError?.name,
            // RevenueCat specific error details
            underlyingErrorMessage: restoreError?.underlyingErrorMessage,
            readableErrorCode: restoreError?.readableErrorCode,
            // Environment
            isAnonymous: auth.currentUser?.isAnonymous,
            platform: Platform.OS,
            isInitialized: this.isInitialized,
          },
        });
        
        throw restoreError; // Re-throw so it reaches outer catch
      }
      
      // Update cache
      this.customerInfoCache = customerInfo;
      this.lastSyncTime = Date.now();

      // Log what was found
      const activeEntitlements = Object.keys(customerInfo.entitlements.active);
      const allEntitlements = Object.keys(customerInfo.entitlements.all);
      
      console.log('[SubscriptionService] ✅ Restore complete:', {
        activeEntitlements: activeEntitlements.length > 0 ? activeEntitlements : 'none',
        allEntitlements: allEntitlements.length > 0 ? allEntitlements : 'none',
        customerId: customerInfo.originalAppUserId,
        firebaseUid: currentFirebaseUid,
      });

      return { success: true, data: customerInfo };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Restore failed';
      // Error already reported in inner catch above with more details
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Check if user has specific tier access
   */
  static hasTierAccess(currentTier: SubscriptionTier, requiredTier: SubscriptionTier): boolean {
    const tierLevels: Record<SubscriptionTier, number> = {
      free: 0,
      standard_premium: 1,
      ai_premium: 2,
    };

    return tierLevels[currentTier] >= tierLevels[requiredTier];
  }

  /**
   * Get current user's subscription tier
   * Convenience method that combines getCustomerInfo + getSubscriptionInfo
   */
  static async getCurrentTier(forceRefresh = false): Promise<ServiceResponse<SubscriptionTier>> {
    try {
      if (Platform.OS === 'web') {
        // Web users default to free tier
        return { success: true, data: 'free' };
      }

      const result = await this.getCustomerInfo(forceRefresh);
      if (!result.success || !result.data) {
        return { success: false, error: result.error };
      }

      const subscriptionInfo = this.getSubscriptionInfo(result.data);
      return { success: true, data: subscriptionInfo.tier };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Get mock offerings for web testing
   * Creates fake packages matching the real RevenueCat structure
   */
  private static getMockOfferings(): PurchasesOfferings {
    const mockPackages = {
      'ai-platinum': {
        identifier: 'ai-platinum',
        availablePackages: [
          {
            identifier: 'ai_premium_monthly',
            packageType: 'MONTHLY',
            product: {
              identifier: 'ai_premium_monthly',
              description: 'AI Platinum Monthly Subscription',
              title: 'Mentorist AI Platinum (Monthly)',
              price: 9.99,
              priceString: '$9.99',
              currencyCode: 'USD',
              introPrice: {
                price: 0,
                priceString: '$0.00',
                cycles: 1,
                period: 'P1W',
                periodUnit: 'WEEK',
                periodNumberOfUnits: 1,
              },
            },
          },
          {
            identifier: 'ai_premium_annual',
            packageType: 'ANNUAL',
            product: {
              identifier: 'ai_premium_annual',
              description: 'AI Platinum Annual Subscription',
              title: 'Mentorist AI Platinum (Annual)',
              price: 79.99,
              priceString: '$79.99',
              currencyCode: 'USD',
              introPrice: {
                price: 0,
                priceString: '$0.00',
                cycles: 1,
                period: 'P1W',
                periodUnit: 'WEEK',
                periodNumberOfUnits: 1,
              },
            },
          },
        ],
      },
      platinum: {
        identifier: 'platinum',
        availablePackages: [
          {
            identifier: 'standard_premium_monthly',
            packageType: 'MONTHLY',
            product: {
              identifier: 'standard_premium_monthly',
              description: 'Platinum Monthly Subscription',
              title: 'Mentorist Platinum (Monthly)',
              price: 6.99,
              priceString: '$6.99',
              currencyCode: 'USD',
              introPrice: {
                price: 0,
                priceString: '$0.00',
                cycles: 1,
                period: 'P1W',
                periodUnit: 'WEEK',
                periodNumberOfUnits: 1,
              },
            },
          },
          {
            identifier: 'standard_premium_annual',
            packageType: 'ANNUAL',
            product: {
              identifier: 'standard_premium_annual',
              description: 'Platinum Annual Subscription',
              title: 'Mentorist Platinum (Annual)',
              price: 59.99,
              priceString: '$59.99',
              currencyCode: 'USD',
              introPrice: {
                price: 0,
                priceString: '$0.00',
                cycles: 1,
                period: 'P1W',
                periodUnit: 'WEEK',
                periodNumberOfUnits: 1,
              },
            },
          },
        ],
      },
    };

    return {
      current: mockPackages['ai-platinum'],
      all: mockPackages,
    };
  }

  /**
   * Check if a package has a free trial or intro offer
   */
  static hasTrialOffer(pkg: PurchasesPackage): boolean {
    if (!pkg) return false;
    
    // Check if product has introPrice (trial/intro offer)
    const product: any = pkg.product || {};
    // RevenueCat exposes introPrice; some platforms expose introductoryPrice
    return !!product.introPrice || !!product.introductoryPrice;
  }

  /**
   * Scan offerings to see if any package has a trial/intro offer
   */
  static offeringHasTrial(offerings: PurchasesOfferings | null): boolean {
    if (!offerings) return false;

    const scanPackages = (pkgs: PurchasesPackage[]) =>
      pkgs.some((p) => this.hasTrialOffer(p));

    if (offerings.current && scanPackages(offerings.current.availablePackages || [])) {
      return true;
    }

    const all = offerings.all || {};
    for (const key of Object.keys(all)) {
      const off: any = (all as any)[key];
      if (off && scanPackages(off.availablePackages || [])) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get trial duration in days from a package
   * Returns null if no trial exists
   */
  static getTrialDays(pkg: PurchasesPackage): number | null {
    if (!this.hasTrialOffer(pkg)) return null;
    
    const introPrice = (pkg.product as any).introPrice || (pkg.product as any).introductoryPrice;
    if (!introPrice) return null;

    // Calculate days based on period unit and number of units
    const unitMultipliers: Record<string, number> = {
      DAY: 1,
      WEEK: 7,
      MONTH: 30, // Approximate
      YEAR: 365, // Approximate
    };

    const multiplier = unitMultipliers[introPrice.periodUnit] || 0;
    return introPrice.periodNumberOfUnits * multiplier;
  }

  /**
   * Check if a package's intro offer is truly a free trial (price = 0)
   */
  static isFreeTrial(pkg: PurchasesPackage): boolean {
    if (!this.hasTrialOffer(pkg)) return false;
    return pkg.product.introPrice?.price === 0;
  }

  /**
   * Get human-readable display name for subscription source
   */
  static getStoreDisplayName(source: string | undefined): string {
    switch (source) {
      case 'app_store':
        return 'App Store';
      case 'play_store':
        return 'Google Play';
      case 'stripe':
        return 'Stripe';
      case 'promotional':
        return 'Promotional';
      case 'unknown':
        return 'Unknown';
      default:
        return 'Unknown';
    }
  }

  /**
   * Clear cache (useful for testing or logout)
   */
  static clearCache(): void {
    this.customerInfoCache = null;
    this.lastSyncTime = 0;
  }

  /**
   * Logout from RevenueCat
   */
  static async logout(): Promise<ServiceResponse<boolean>> {
    try {
      if (Platform.OS === 'web') {
        return { success: true, data: true };
      }

      if (!this.isInitialized) {
        return { success: true, data: true };
      }

      await Purchases.logOut();
      this.clearCache();
      
      console.log('[SubscriptionService] User logged out from RevenueCat');
      return { success: true, data: true };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Logout failed';
      errorReporting.reportError(error, {
        screen: 'SubscriptionService',
        action: 'logout',
      });
      return { success: false, error: errorMessage };
    }
  }
}

